import React, { useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mail, Github, Linkedin, ArrowUpRight, ExternalLink } from "lucide-react";
import { projects, experience, fadeUp, scaleIn, ScrollProgress } from "./portfolioData";

// ─────────────────────────────────────────────────────────────────────
// Background
// ─────────────────────────────────────────────────────────────────────

const MeshBackground = () => {
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y4 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-[#0c0618]" />
      <motion.div style={{ y: y1 }} className="absolute -top-[10%] -left-[10%] h-[600px] w-[600px] rounded-full bg-indigo-600/30 blur-[120px] animate-float-1" />
      <motion.div style={{ y: y2 }} className="absolute top-[20%] -right-[5%] h-[500px] w-[500px] rounded-full bg-purple-600/25 blur-[100px] animate-float-2" />
      <motion.div style={{ y: y3 }} className="absolute top-[50%] -left-[15%] h-[550px] w-[550px] rounded-full bg-pink-600/20 blur-[110px] animate-float-3" />
      <motion.div style={{ y: y4 }} className="absolute top-[70%] right-[10%] h-[450px] w-[450px] rounded-full bg-teal-500/15 blur-[100px] animate-float-4" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(12,6,24,0.5)_70%)]" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────────────

const AnimatedGradientText = ({ children }) => (
  <span className="text-6xl font-extrabold tracking-tight md:text-7xl lg:text-8xl bg-gradient-to-r from-indigo-300 via-purple-400 to-pink-400 bg-[length:200%_200%] bg-clip-text text-transparent animate-text-gradient">
    {children}
  </span>
);

const SectionHeader = ({ children }) => (
  <motion.h2
    className="mb-12 text-center text-3xl font-bold tracking-tight md:text-4xl"
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
  >
    <span className="section-accent">{children}</span>
  </motion.h2>
);

const ProjectCard = ({ project, index }) => {
  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--spotlight-x", `${x}%`);
    e.currentTarget.style.setProperty("--spotlight-y", `${y}%`);
  }, []);

  return (
    <motion.article
      className="card-spotlight gradient-border group relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:bg-white/[0.06] hover:shadow-2xl hover:shadow-purple-500/10"
      variants={scaleIn}
      custom={index}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
    >
      <h3 className="relative z-10 flex items-center text-xl font-semibold">
        {project.name}
        {project.url && (
          <ArrowUpRight className="ml-1.5 h-4 w-4 opacity-50 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        )}
      </h3>
      <p className="relative z-10 mt-3 text-sm leading-relaxed text-slate-300/90">{project.description}</p>
      <div className="relative z-10 mt-5 flex flex-wrap gap-2">
        {project.tags.map((tag) => (
          <span key={tag} className="rounded-full bg-gradient-to-r from-purple-500/15 to-pink-500/15 border border-white/10 px-3 py-1 text-xs font-medium tracking-wide text-white/75">
            {tag}
          </span>
        ))}
      </div>
      {project.url && (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a href={project.url} target="_blank" rel="noreferrer" aria-label={`View ${project.name}`} className="absolute inset-0 z-20 rounded-2xl" />
      )}
    </motion.article>
  );
};

const ExperienceItem = ({ exp, index, isLast }) => (
  <motion.li
    className="relative pl-10"
    variants={fadeUp}
    custom={index}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
  >
    <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-teal-400 bg-teal-400/30 animate-pulse-dot" />
    {!isLast && (
      <span className="absolute left-[7px] top-6 bottom-[-40px] w-[2px] bg-gradient-to-b from-teal-400/40 to-purple-500/10" />
    )}
    <div className="text-lg font-semibold leading-snug">
      {exp.role} <span className="text-purple-300/80 font-medium">· {exp.company}</span>
    </div>
    <div className="mt-1 text-xs font-medium uppercase tracking-widest text-white/50">{exp.period}</div>
    <p className="mt-2.5 text-sm leading-relaxed text-slate-300/90">{exp.description}</p>
  </motion.li>
);

// ─────────────────────────────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────────────────────────────

const PortfolioClassic = () => (
  <div className="noise-overlay relative min-h-screen w-full overflow-x-hidden pb-32 text-white">
    <MeshBackground />
    <ScrollProgress />

    <header className="relative z-10 mx-auto max-w-5xl px-6 pt-32 md:pt-40 text-center">
      <motion.div variants={fadeUp} initial="hidden" animate="show">
        <AnimatedGradientText>William Soylemez</AnimatedGradientText>
      </motion.div>

      <motion.p
        className="mx-auto mt-8 max-w-xl text-lg leading-relaxed text-slate-300/90 md:text-xl"
        variants={fadeUp}
        custom={2}
        initial="hidden"
        animate="show"
      >
        CS &times; Math &times; Env Studies @ Tufts. Building tools that help
        people &amp; planet. Full&#8209;stack, cloud computing, networks.
      </motion.p>

      <motion.nav className="mt-10 flex justify-center gap-5" variants={fadeUp} custom={3} initial="hidden" animate="show">
        <a href="mailto:william.soylemez@gmail.com" aria-label="email" className="social-icon">
          <Mail className="h-5 w-5" />
        </a>
        <a href="https://github.com/william-soylemez" target="_blank" rel="noreferrer" aria-label="github" className="social-icon">
          <Github className="h-5 w-5" />
        </a>
        <a href="https://www.linkedin.com/in/william-soylemez-307709255/" target="_blank" rel="noreferrer" aria-label="linkedin" className="social-icon">
          <Linkedin className="h-5 w-5" />
        </a>
      </motion.nav>
    </header>

    <section className="relative z-10 mx-auto mt-36 max-w-4xl px-6">
      <SectionHeader>About Me</SectionHeader>
      <motion.div
        className="card-spotlight gradient-border rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-lg leading-relaxed backdrop-blur-md text-slate-200/90"
        variants={fadeUp}
        custom={1}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        Hi everyone! I'm Will, a rising Senior at Tufts University studying
        Computer Science, Environmental Studies, and Mathematics. I'm
        passionate about using technical skills to solve pressing problems and
        make life better for real people. In my free time, I like to cook,
        read, run, and mix cocktails.
      </motion.div>
    </section>

    <section className="relative z-10 mx-auto mt-36 max-w-5xl px-6">
      <SectionHeader>Projects</SectionHeader>
      <div className="grid gap-8 md:grid-cols-2">
        {projects.map((project, i) => (
          <ProjectCard key={i} project={project} index={i + 1} />
        ))}
      </div>
    </section>

    <section className="relative z-10 mx-auto mt-36 max-w-4xl px-6">
      <SectionHeader>Experience</SectionHeader>
      <ul className="space-y-12">
        {experience.map((exp, i) => (
          <ExperienceItem key={i} exp={exp} index={i + 1} isLast={i === experience.length - 1} />
        ))}
      </ul>
    </section>

    <motion.div
      className="relative z-10 mx-auto mt-40 max-w-max"
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      <a
        href="/resume.pdf"
        target="_blank"
        rel="noreferrer"
        className="shimmer-btn group flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-105"
      >
        View Résumé
        <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      </a>
    </motion.div>

    <footer className="relative z-10 mx-auto mt-28 max-w-4xl px-6 text-center">
      <div className="mx-auto mb-6 h-px w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <p className="text-sm text-white/40 tracking-wide">
        &copy; {new Date().getFullYear()} William Soylemez &middot; Built with
        React + Tailwind + Framer&nbsp;Motion
      </p>
    </footer>
  </div>
);

export default PortfolioClassic;
